import { useAuth } from '@clerk/react'
import * as Tooltip from '@radix-ui/react-tooltip'
import { Shuffle } from 'lucide-react'
import toast from 'react-hot-toast'
import { useLocation } from '#router'
import { isSolvedStatus } from '@/lib/progress-status'
import { trpc } from '@/lib/trpc'

export type ShuffleButtonProblem = {
  id: string
  slug: string
}

export type ShuffleButtonProps = {
  problems: ShuffleButtonProblem[]
  listId?: string
  ariaLabel?: string
  className?: string
}

export function ShuffleButton({
  problems,
  listId,
  ariaLabel,
  className,
}: ShuffleButtonProps) {
  const { isSignedIn } = useAuth()
  const { navigate } = useLocation()

  const progressQuery = trpc.problems.getUserProgress.useQuery(undefined, {
    enabled: !!isSignedIn,
  })

  const isProgressLoading = !!isSignedIn && progressQuery.isLoading
  const disabled = problems.length === 0 || isProgressLoading

  const tooltipText = (() => {
    if (!isSignedIn) return 'Pick One'
    const progress = progressQuery.data?.progress
    if (!progress) return 'Pick One'
    const hasUnsolved = problems.some((p) => !isSolvedStatus(progress[p.id]))
    return hasUnsolved ? 'Pick One: Unsolved' : 'Pick One'
  })()

  const showRetryToast = () => {
    toast.custom((t) => (
      <div className="border-border bg-background text-foreground flex items-center gap-3 rounded-md border px-3 py-2 text-sm shadow-md">
        <span>Couldn't load your progress</span>
        <button
          type="button"
          className="text-primary hover:underline text-sm font-medium"
          onClick={() => {
            progressQuery.refetch()
            toast.dismiss(t.id)
          }}
        >
          Retry
        </button>
      </div>
    ))
  }

  const handleClick = () => {
    if (disabled) return
    if (isSignedIn && progressQuery.isError) {
      showRetryToast()
      return
    }
    const progress = progressQuery.data?.progress
    let pool = problems
    if (progress) {
      const unsolved = problems.filter((p) => !isSolvedStatus(progress[p.id]))
      if (unsolved.length > 0) pool = unsolved
    }
    const picked = pool[Math.floor(Math.random() * pool.length)]
    const url = listId
      ? `/editor?problem=${picked.slug}&list=${listId}`
      : `/editor?problem=${picked.slug}`
    navigate(url)
  }

  return (
    <Tooltip.Provider delayDuration={150}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <span className="inline-flex" tabIndex={disabled ? 0 : -1}>
            <button
              type="button"
              aria-label={ariaLabel}
              className={
                className ??
                'text-muted-foreground hover:text-foreground p-2 transition-colors'
              }
              disabled={disabled}
              onClick={handleClick}
            >
              <Shuffle className="h-4 w-4" />
            </button>
          </span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="top"
            align="center"
            className="border-border bg-background text-foreground z-50 rounded-md border px-3 py-1.5 text-sm font-medium shadow-md"
          >
            {tooltipText}
            <Tooltip.Arrow className="fill-background" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  )
}
